import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  Card,
  Typography,
  Tag,
  Space,
  Flex,
  InputNumber,
  Alert,
  App,
  Drawer,
  Descriptions,
  Divider,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import RobinPage from "../components/RobinPage";
import AddTransactionModal from "../components/AddTransactionModal";
import { useApolloClient } from "@apollo/client";
import { 
  useGetAllPartnersSelectQuery,
  LedgerTransactionsDocument,
  DownloadLedgerTransactionsDocument,
} from "../graphql/generated/hooks";
import { usePermission } from "../hooks/usePermission";
import { DollarCircleOutlined, DownloadOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// Transaction status enum
enum TransactionStatus {
  PENDING = "PENDING",
  POSTED = "POSTED",
  CANCELLED = "CANCELLED",
}

// Transaction type enum
enum TransactionType {
  ORDER_CAPTURE = "ORDER_CAPTURE",
  PLATFORM_COMMISSION = "PLATFORM_COMMISSION",
  DELIVERY_FEE = "DELIVERY_FEE",
  GATEWAY_FEE = "GATEWAY_FEE",
  PAYOUT = "PAYOUT",
  REFUND = "REFUND",
  ADJUSTMENT = "ADJUSTMENT",
  TIP = "TIP",
  PARTNER_DELIVERY_COVERAGE = "PARTNER_DELIVERY_COVERAGE",
  DISCOUNT_PARTNER_COVERAGE = "DISCOUNT_PARTNER_COVERAGE",
  ADDITIONAL_CUSTOMER_FEE = "ADDITIONAL_CUSTOMER_FEE",
  FAST_DELIVERY_FEE = "FAST_DELIVERY_FEE",
  DISTANCE_FEE = "DISTANCE_FEE",
}

// Amount sign filter
enum AmountSign {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
}

// Sort options
enum SortBy {
  AMOUNT = "AMOUNT",
  CREATED_AT = "CREATED_AT",
}

enum SortOrder {
  ASC = "ASC",
  DESC = "DESC",
}

// GraphQL types - these match your query structure (commented interfaces are for reference only)
// interface PaginationInput {
//   limit: number;
//   offset: number;
// }

interface LedgerTransactionsFilter {
  search?: string | null;
  amountSign?: AmountSign | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  statuses?: TransactionStatus[] | null;
  types?: TransactionType[] | null;
  orderId?: number | null;
  partnerId?: number | null;
}

// interface LedgerTransactionSortInput {
//   sortBy: SortBy;
//   sortOrder: SortOrder;
// }

interface LedgerTransaction {
  amountMinor: string;
  availableAt: string;
  correlationId: string;
  createdAt: string;
  description: string;
  id: string;
  idempotencyKey?: string | null;
  orderId?: number | null;
  postedAt: string;
  status: string;
  storeId: number;
  type: string;
  partner: {
    ar: string;
    en: string;
  };
  store: {
    ar: string;
    en: string;
  };
}

interface LedgerTransactionsResponse {
  hasMore: boolean;
  total: number;
  items: LedgerTransaction[];
}

// Local component state interfaces
interface FilterState {
  search: string | null;
  amountSign: AmountSign | null;
  minAmount: number | null;
  maxAmount: number | null;
  createdFrom: string | null;
  createdTo: string | null;
  statuses: TransactionStatus[];
  types: TransactionType[];
  orderId: number | null;
  partnerId: number | null;
}

interface SortState {
  sortBy: SortBy;
  sortOrder: SortOrder;
}

interface PaginationState {
  current: number;
  pageSize: number;
  total: number;
}

const OrderTransactionsDetails = ({ orderId, t, formatAmount, getStatusColor, getTypeColor }: any) => {
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<LedgerTransaction[]>([]);

  useEffect(() => {
    let mounted = true;
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const { data } = await apolloClient.query({
          query: LedgerTransactionsDocument,
          variables: {
            pagination: { limit: 100, offset: 0 },
            filter: { orderId }
          },
          fetchPolicy: 'network-only'
        });
        if (mounted && data?.ledgerTransactions?.items) {
          const sortedItems = [...data.ledgerTransactions.items].sort((a, b) => {
            if (Number(a.amountMinor) > 0 && Number(b.amountMinor) <= 0) return -1;
            if (Number(a.amountMinor) <= 0 && Number(b.amountMinor) > 0) return 1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
          setItems(sortedItems);
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (orderId) {
      fetchTransactions();
    }
    return () => { mounted = false; };
  }, [orderId, apolloClient]);

  const columns: ColumnsType<LedgerTransaction> = [
    {
      title: t("label.transaction.id"),
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("label.transaction.amount"),
      dataIndex: "amountMinor",
      key: "amountMinor",
      width: 120,
      render: formatAmount,
    },
    {
      title: t("label.transaction.type"),
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>
          {t(`transaction.type.${type.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t("label.transaction.status"),
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {t(`transaction.status.${status.toLowerCase()}`)}
        </Tag>
      ),
    },
  ];

  const totalAmount = items.reduce((acc, item) => acc + Number(item.amountMinor), 0);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={5} style={{ margin: 0 }}>
        {t("label.transaction_details") || "Transaction Details"}
      </Typography.Title>
      <Alert 
        message={<Typography.Text strong>{t("label.total_amount") || "Total Amount"}: {formatAmount(String(totalAmount))}</Typography.Text>}
        type={totalAmount >= 0 ? "success" : "error"}
        showIcon
      />
      <Table 
        size="small"
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
    </Space>
  );
};

function TransactionsList() {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const apolloClient = useApolloClient();
  const { data: partnersData, loading: partnersLoading } =
    useGetAllPartnersSelectQuery();
  
  // Check permissions - try common permission names for ledger transactions
  const permissions = usePermission([
    "getAllLedgerTransactions", 
    "getLedgerTransactions", 
    "viewLedgerTransactions",
    "ledgerTransactions",
    "getAllTransactions",
    "getTransactions",
    "viewTransactions",
    "addLedgerTransaction",
    "createTransaction"
  ]);
  
  // Check if user has any of the possible permissions
  const hasPermission = Object.values(permissions).some(Boolean);

  // State management
  const [filters, setFilters] = useState<FilterState>({
    search: null,
    amountSign: null,
    minAmount: null,
    maxAmount: null,
    createdFrom: null,
    createdTo: null,
    statuses: [],
    types: [TransactionType.ORDER_CAPTURE],
    orderId: null,
    partnerId: null,
  });

  const [sort, setSort] = useState<SortState>({
    sortBy: SortBy.CREATED_AT,
    sortOrder: SortOrder.DESC,
  });

  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [data, setData] = useState<{ ledgerTransactions: LedgerTransactionsResponse } | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<LedgerTransaction | null>(null);

  // Helper function to build filter input that matches GraphQL schema
  const buildFilterInput = (filters: FilterState): LedgerTransactionsFilter => {
    return {
      search: filters.search,
      amountSign: filters.amountSign,
      minAmount: filters.minAmount,
      maxAmount: filters.maxAmount,
      createdFrom: filters.createdFrom,
      createdTo: filters.createdTo,
      statuses: filters.statuses.length > 0 ? filters.statuses : null,
      types: filters.types.length > 0 ? filters.types : null,
      orderId: filters.orderId,
      partnerId: filters.partnerId,
    };
  };

  // Make real GraphQL call using Apollo Client with authorization
  const fetchTransactions = useCallback(async (
    isInitialLoad = false,
    overridePagination?: { current: number, pageSize: number },
    overrideSort?: SortState
  ) => {
    setLoading(true);
    
    // Use override config if provided (for immediate handleTableChange usage), else use state
    const currentPagination = overridePagination || pagination;
    const currentSort = overrideSort || sort;

    try {
      // Make actual GraphQL query with proper authorization
      const { data: gqlData } = await apolloClient.query({
        query: LedgerTransactionsDocument,
        variables: {
          pagination: {
            limit: currentPagination.pageSize,
            offset: (currentPagination.current - 1) * currentPagination.pageSize,
          },
          filter: isInitialLoad ? { types: [TransactionType.ORDER_CAPTURE] } : buildFilterInput(filters),
          sort: {
            sortBy: currentSort.sortBy,
            sortOrder: currentSort.sortOrder
          }
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });
      
      // Set the data from GraphQL response
      setData(gqlData);
      setHasSearched(true);
      if (isInitialLoad) {
        setInitialLoad(false);
      }
      
    } catch (error: any) {
      console.error('Error fetching ledger transactions:', error);
      
      // Check if it's a GraphQL error or network error
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        // Handle GraphQL errors (like query not implemented yet)
        const gqlError = error.graphQLErrors[0];
        if (gqlError.message.includes('Cannot query field') || gqlError.message.includes('Unknown')) {
          message.warning(t('error.ledger_transactions_not_implemented'));
          console.warn('Ledger transactions query not implemented on backend yet');
        } else {
          message.error(gqlError.message || t('error.failed_to_fetch_transactions'));
        }
      } else if (error.networkError) {
        // Handle network errors
        message.error(t('error.network_error'));
      } else {
        // Handle other errors
        message.error(error.message || t('error.failed_to_fetch_transactions'));
      }
      
      // Set empty data on error
      setData({
        ledgerTransactions: {
          hasMore: false,
          total: 0,
          items: [],
        }
      });
      setHasSearched(true);
      if (isInitialLoad) {
        setInitialLoad(false);
      }
    } finally {
      setLoading(false);
    }
  }, [apolloClient, pagination, sort, filters, t, message]);

  // Download transactions with current filters
  const downloadTransactions = useCallback(async () => {
    setDownloading(true);
    try {
      // Make GraphQL query for download
      const { data: downloadData } = await apolloClient.query({
        query: DownloadLedgerTransactionsDocument,
        variables: {
          filter: buildFilterInput(filters),
        },
        fetchPolicy: 'network-only',
      });

      if (downloadData?.downloadLedgerTransactions) {
        const { fileContent, filename, mimeType } = downloadData.downloadLedgerTransactions;
        
        // Decode base64 content
        const binaryString = window.atob(fileContent);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Create blob and download
        const blob = new Blob([bytes], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        message.success(t('success.file_downloaded'));
      }
    } catch (error: any) {
      console.error('Error downloading ledger transactions:', error);
      
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const gqlError = error.graphQLErrors[0];
        message.error(gqlError.message || t('error.failed_to_download'));
      } else if (error.networkError) {
        message.error(t('error.network_error'));
      } else {
        message.error(error.message || t('error.failed_to_download'));
      }
    } finally {
      setDownloading(false);
    }
  }, [apolloClient, filters, t, message]);

  // Load data on component mount (only if user has permission)
  useEffect(() => {
    if (initialLoad && hasPermission) {
      fetchTransactions(true);
    }
  }, [initialLoad, fetchTransactions, hasPermission]);

  // Update pagination total when data changes
  useEffect(() => {
    if (data?.ledgerTransactions) {
      setPagination(prev => ({
        ...prev,
        total: data.ledgerTransactions.total,
      }));
    }
  }, [data]);

  // Filter handlers
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
  };

  const handleSortChange = (newSort: Partial<SortState>) => {
    setSort(prev => ({ ...prev, ...newSort }));
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
  };

  const handleTableChange = (paginationConfig: TablePaginationConfig, filters: any, sorter: any) => {
    let nextPagination = { current: pagination.current, pageSize: pagination.pageSize };
    
    // Handle pagination
    if (paginationConfig.current && paginationConfig.pageSize) {
      nextPagination = {
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };
      setPagination(prev => ({
        ...prev,
        ...nextPagination,
      }));
    }

    let nextSort = { ...sort };
    // Handle sorting from table headers
    if (sorter && sorter.field) {
      const sortByMap: Record<string, SortBy> = {
        amountMinor: SortBy.AMOUNT,
        createdAt: SortBy.CREATED_AT,
      };

      const sortBy = sortByMap[sorter.field] || SortBy.CREATED_AT;
      const sortOrder = sorter.order === 'ascend' ? SortOrder.ASC : SortOrder.DESC;
      
      nextSort = { sortBy, sortOrder };
      setSort(nextSort);
    }

    // Only refetch if we have already searched before
    if (hasSearched) {
      // Pass the pagination and sort explicit overrides immediately to avoid React state race conditions.
      fetchTransactions(false, nextPagination, nextSort);
    }
  };

  const resetFilters = () => {
    setFilters({
      search: null,
      amountSign: null,
      minAmount: null,
      maxAmount: null,
      createdFrom: null,
      createdTo: null,
      statuses: [],
      types: [TransactionType.ORDER_CAPTURE],
      orderId: null,
      partnerId: null,
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    // Load all data without filters (like initial load)
    fetchTransactions(true);
  };

  // Modal handlers
  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    fetchTransactions(); // Refresh the list after successful creation
  };

  // Format currency amount
  const formatAmount = (amountMinor: string) => {
    const amount = parseFloat(amountMinor);
    const isNegative = amount < 0;
    const absoluteAmount = Math.abs(amount);
    
    return (
      <Text type={isNegative ? "danger" : "success"}>
        {isNegative ? "-" : "+"}{absoluteAmount.toFixed(2)} {t("label.currency")}
      </Text>
    );
  };

  // Format date
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("YYYY-MM-DD HH:mm:ss");
  };

  // Get status tag color
  const getStatusColor = (status: string) => {
    switch (status) {
      case TransactionStatus.POSTED:
        return "green";
      case TransactionStatus.PENDING:
        return "orange";
      case TransactionStatus.CANCELLED:
        return "red";
      default:
        return "default";
    }
  };

  // Get transaction type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case TransactionType.ORDER_CAPTURE:
        return "blue";
      case TransactionType.PLATFORM_COMMISSION:
        return "red";
      case TransactionType.DELIVERY_FEE:
        return "purple";
      case TransactionType.GATEWAY_FEE:
        return "orange";
      case TransactionType.PAYOUT:
        return "geekblue";
      case TransactionType.REFUND:
        return "magenta";
      case TransactionType.ADJUSTMENT:
        return "cyan";
      case TransactionType.TIP:
        return "green";
      case TransactionType.PARTNER_DELIVERY_COVERAGE:
        return "volcano";
      case TransactionType.DISCOUNT_PARTNER_COVERAGE:
        return "gold";
      case TransactionType.ADDITIONAL_CUSTOMER_FEE:
        return "lime";
      case TransactionType.FAST_DELIVERY_FEE:
        return "purple";
      case TransactionType.DISTANCE_FEE:
        return "blue";
      default:
        return "default";
    }
  };

  // Table columns
  const columns: ColumnsType<LedgerTransaction> = [
    {
      title: t("label.transaction.order_id"),
      dataIndex: "orderId",
      key: "orderId",
      width: 120,
      render: (orderId: number) => orderId ? (
        <Space>
          <Typography.Text strong style={{ color: '#1677ff' }}>#{orderId}</Typography.Text>
        </Space>
      ) : (
        <Typography.Text type="secondary">-</Typography.Text>
      ),
    },
    {
      title: t("label.transaction.amount"),
      dataIndex: "amountMinor",
      key: "amountMinor",
      width: 120,
      render: formatAmount,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: t("label.transaction.type"),
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>
          {t(`transaction.type.${type.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t("label.transaction.status"),
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {t(`transaction.status.${status.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t("label.transaction.description"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: t("label.transaction.partner_name"),
      dataIndex: "partner",
      key: "partner",
      width: 150,
      render: (partner: { ar: string; en: string }) => {
        const currentLang = i18n.language;
        const partnerName = currentLang === 'ar' ? partner.ar : partner.en;
        return partnerName || partner.en || partner.ar; // Fallback to available language
      },
    },
    {
      title: t("label.transaction.store_name"),
      dataIndex: "store",
      key: "store",
      width: 150,
      render: (store: { ar: string; en: string }) => {
        const currentLang = i18n.language;
        const storeName = currentLang === 'ar' ? store.ar : store.en;
        return storeName || store.en || store.ar; // Fallback to available language
      },
    },
    {
      title: t("label.transaction.created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: formatDate,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
  ];

  // Show permission error if user doesn't have access
  if (!hasPermission) {
    return (
      <RobinPage title={t("label.menu.transactions")}>
        <Alert
          message={t("error.access_denied")}
          description={t("error.insufficient_permissions")}
          type="error"
          showIcon
          style={{ margin: "20px 0" }}
        />
      </RobinPage>
    );
  }

  return (
    <RobinPage title={t("label.menu.transactions")}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Add Transaction Button */}
        <Card 
          style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
          }}
          styles={{ body: { padding: '24px' } }}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap="small">
            <Space direction="vertical" size={0}>
              <Typography.Title 
                level={4} 
                style={{ 
                  margin: 0, 
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                {t("label.menu.transactions")}
              </Typography.Title>
              <Typography.Text 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '14px',
                }}
              >
                {t("label.transactions_description")}
              </Typography.Text>
            </Space>
            <Space size="middle">
              <Button 
                type="default"
                icon={<DownloadOutlined style={{ fontSize: '18px' }} />}
                onClick={downloadTransactions}
                loading={downloading}
                size="large"
                style={{
                  height: '48px',
                  padding: '0 24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
              >
                {t("button.download")}
              </Button>
              <Button 
                type="primary"
                icon={<DollarCircleOutlined style={{ fontSize: '18px' }} />}
                onClick={showModal}
                size="large"
                style={{
                  height: '48px',
                  padding: '0 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                  background: '#fff',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
              >
                {t("button.add_transaction")}
              </Button>
            </Space>
          </Flex>
        </Card>

        {/* Filters */}
        <Card title={t("label.filters")} size="small">
          <Row gutter={[16, 16]}>
            {/* Search */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input.Search
                placeholder={t("placeholder.search_transactions")}
                allowClear
                value={filters.search || ""}
                onChange={(e) => handleFilterChange("search", e.target.value || null)}
                onSearch={() => fetchTransactions()}
              />
            </Col>

            {/* Transaction Types */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                mode="multiple"
                placeholder={t("label.transaction.type")}
                allowClear
                style={{ width: "100%" }}
                value={filters.types}
                onChange={(value) => handleFilterChange("types", value)}
              >
                {Object.values(TransactionType).map((type) => (
                  <Option key={type} value={type}>
                    {t(`transaction.type.${type.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Transaction Status */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                mode="multiple"
                placeholder={t("label.transaction.status")}
                allowClear
                style={{ width: "100%" }}
                value={filters.statuses}
                onChange={(value) => handleFilterChange("statuses", value)}
              >
                {Object.values(TransactionStatus).map((status) => (
                  <Option key={status} value={status}>
                    {t(`transaction.status.${status.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Amount Sign */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.transaction.amount_sign")}
                allowClear
                style={{ width: "100%" }}
                value={filters.amountSign}
                onChange={(value) => handleFilterChange("amountSign", value)}
              >
                <Option value={AmountSign.POSITIVE}>{t("label.positive")}</Option>
                <Option value={AmountSign.NEGATIVE}>{t("label.negative")}</Option>
              </Select>
            </Col>

            {/* Min Amount */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <InputNumber
                placeholder={t("label.transaction.min_amount")}
                style={{ width: "100%" }}
                value={filters.minAmount}
                onChange={(value) => handleFilterChange("minAmount", value)}
                addonAfter={t("label.currency")}
                step={0.01}
                precision={2}
              />
            </Col>

            {/* Max Amount */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <InputNumber
                placeholder={t("label.transaction.max_amount")}
                style={{ width: "100%" }}
                value={filters.maxAmount}
                onChange={(value) => handleFilterChange("maxAmount", value)}
                addonAfter={t("label.currency")}
                step={0.01}
                precision={2}
              />
            </Col>

            {/* Order ID */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <InputNumber
                placeholder={t("label.transaction.order_id")}
                style={{ width: "100%" }}
                value={filters.orderId}
                onChange={(value) => handleFilterChange("orderId", value)}
              />
            </Col>

            {/* Partner Selection */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.transaction.partner_name")}
                allowClear
                style={{ width: "100%" }}
                value={filters.partnerId}
                onChange={(value) => handleFilterChange("partnerId", value)}
                loading={partnersLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={partnersData?.getAllPartners?.map((partner) => ({
                  label: `${partner.name} (${partner.nameAr}) - ID: ${partner.id}`,
                  value: partner.id,
                }))}
              />
            </Col>

            {/* Created Date Range */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                placeholder={[t("label.transaction.created_from"), t("label.transaction.created_to")]}
                style={{ width: "100%" }}
                value={
                  filters.createdFrom && filters.createdTo
                    ? [dayjs(filters.createdFrom), dayjs(filters.createdTo)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    handleFilterChange("createdFrom", dates[0].toISOString());
                    handleFilterChange("createdTo", dates[1].toISOString());
                  } else {
                    handleFilterChange("createdFrom", null);
                    handleFilterChange("createdTo", null);
                  }
                }}
                showTime
              />
            </Col>

            {/* Sort Controls */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.sort_by")}
                style={{ width: "100%" }}
                value={sort.sortBy}
                onChange={(value) => handleSortChange({ sortBy: value })}
              >
                <Option value={SortBy.AMOUNT}>{t("label.transaction.amount")}</Option>
                <Option value={SortBy.CREATED_AT}>{t("label.transaction.created_at")}</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.sort_order")}
                style={{ width: "100%" }}
                value={sort.sortOrder}
                onChange={(value) => handleSortChange({ sortOrder: value })}
              >
                <Option value={SortOrder.ASC}>{t("label.ascending")}</Option>
                <Option value={SortOrder.DESC}>{t("label.descending")}</Option>
              </Select>
            </Col>

            {/* Action Buttons */}
            <Col xs={24} sm={24} md={24} lg={24}>
              <Flex gap="small" wrap="wrap">
                <Button 
                  type="primary" 
                  onClick={() => fetchTransactions()}
                  loading={loading}
                >
                  {t("button.search")}
                </Button>
                <Button onClick={resetFilters}>
                  {t("button.reset_filters")}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Card>

        {/* Transactions Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={data?.ledgerTransactions?.items || []}
            loading={loading}
            rowKey="id"
            rowClassName={() => 'cursor-pointer hover-row'}
            onRow={(record) => {
              return {
                onClick: () => {
                  setSelectedTransaction(record);
                  setDrawerVisible(true);
                },
                style: { cursor: 'pointer' },
              };
            }}
            scroll={{ x: 1400 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t("label.of")} ${total} ${t("label.transactions")}`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableChange}
          />
        </Card>

        {/* Add Transaction Modal */}
        <AddTransactionModal
          open={isModalOpen}
          onCancel={handleModalClose}
          onSuccess={handleModalSuccess}
        />

        {/* Transaction Details Drawer */}
        <Drawer
          title={selectedTransaction?.orderId 
             ? `${t("label.transaction.order_id") || "Order ID"} #${selectedTransaction.orderId}`
             : (t("label.transaction_details") || "Transaction Details")}
          placement="right"
          width={800}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
        >
          {selectedTransaction && (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label={t("label.transaction.order_id")}>
                  {selectedTransaction.orderId ? (
                    <Typography.Text strong copyable style={{ color: '#1677ff' }}>#{selectedTransaction.orderId}</Typography.Text>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t("label.transaction.partner_name")}>
                  {(() => {
                    const currentLang = i18n.language;
                    const partnerName = currentLang === 'ar' ? selectedTransaction.partner?.ar : selectedTransaction.partner?.en;
                    return partnerName || selectedTransaction.partner?.en || selectedTransaction.partner?.ar || '-';
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t("label.transaction.store_name")}>
                  {(() => {
                    const currentLang = i18n.language;
                    const storeName = currentLang === 'ar' ? selectedTransaction.store?.ar : selectedTransaction.store?.en;
                    return storeName || selectedTransaction.store?.en || selectedTransaction.store?.ar || '-';
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t("label.transaction.created_at")}>
                  {formatDate(selectedTransaction.createdAt)}
                </Descriptions.Item>
              </Descriptions>

              <Divider style={{ margin: '0' }} />

              {selectedTransaction.orderId ? (
                <OrderTransactionsDetails 
                  orderId={selectedTransaction.orderId} 
                  t={t}
                  formatAmount={formatAmount}
                  getStatusColor={getStatusColor}
                  getTypeColor={getTypeColor}
                />
              ) : (
                <>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label={t("label.transaction.id") || "Transaction ID"}>
                      <Typography.Text copyable>{selectedTransaction.id}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("label.transaction.amount")}>
                     {formatAmount(selectedTransaction.amountMinor)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("label.transaction.type")}>
                      <Tag color={getTypeColor(selectedTransaction.type)}>
                        {t(`transaction.type.${selectedTransaction.type.toLowerCase()}`)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("label.transaction.status")}>
                      <Tag color={getStatusColor(selectedTransaction.status)}>
                        {t(`transaction.status.${selectedTransaction.status.toLowerCase()}`)}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>

                  <Typography.Title level={5}>{t("label.transaction.description")}</Typography.Title>
                  <Typography.Paragraph>
                    {selectedTransaction.description || '-'}
                  </Typography.Paragraph>

                  {selectedTransaction.correlationId && (
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label={t("label.correlation_id") || "Correlation ID"}>
                        <Typography.Text copyable>{selectedTransaction.correlationId}</Typography.Text>
                      </Descriptions.Item>
                      {selectedTransaction.idempotencyKey && (
                        <Descriptions.Item label={t("label.idempotency_key") || "Idempotency Key"}>
                          <Typography.Text copyable>{selectedTransaction.idempotencyKey}</Typography.Text>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label={t("label.posted_at") || "Posted At"}>
                        {selectedTransaction.postedAt ? formatDate(selectedTransaction.postedAt) : '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </>
              )}
            </Space>
          )}
        </Drawer>
      </Space>
    </RobinPage>
  );
}

export default TransactionsList;
